import { useEffect, useRef } from 'react';
import { cn } from '../../lib/cn';
import type { HashLinkHit, HashLinkSection } from '../../lib/messaging/hashLink';

interface HashLinkAutocompleteProps {
  sections: HashLinkSection[];
  activeIndex: number;
  onSelect: (hit: HashLinkHit) => void;
  onClose: () => void;
}

export function HashLinkAutocomplete({
  sections,
  activeIndex,
  onSelect,
  onClose,
}: HashLinkAutocompleteProps) {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (listRef.current && !listRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  if (!sections.length) return null;

  let cursor = 0;

  return (
    <div
      ref={listRef}
      className="absolute bottom-full mb-1 left-0 w-72 bg-cime-surface border border-cime-border rounded-lg shadow-lg overflow-hidden z-50 max-h-72 overflow-y-auto"
    >
      {sections.map((section) => (
        <div key={section.kind}>
          <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-cime-text-muted bg-cime-bg">
            {section.label}
          </div>
          {section.hits.map((hit) => {
            const index = cursor;
            cursor += 1;
            const icon = hit.kind === 'dossier' ? '/icons/folder.svg' : '/icons/building.svg';
            return (
              <button
                key={`${hit.kind}-${hit.id}`}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSelect(hit);
                }}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-2 text-sm text-left',
                  index === activeIndex
                    ? 'bg-cime-sidebar-active-bg'
                    : 'hover:bg-cime-sidebar-active-bg',
                  'transition-colors',
                )}
              >
                <img src={icon} alt="" className="w-4 h-4 flex-shrink-0 opacity-70" />
                <span className="min-w-0 flex-1">
                  <span className="text-cime-text-primary truncate block">{hit.title}</span>
                  {(hit.subtitle || hit.hint) && (
                    <span className="text-[11px] text-cime-text-muted truncate block">
                      {[hit.subtitle, hit.hint].filter(Boolean).join(' · ')}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
